import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { Observable, throwError } from "rxjs";
import { catchError, shareReplay } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PadlockTokenState } from "./state/padlock-token.reducer";
import { selectPadlockTokenState } from "./state/padlock-token.selectors";

export interface PadlockUser {
  uuid: string;
  email: string;
}

export interface PadlockRole {
  uuid: string;
  name: string;
  description: string;
  tenant_uuid: string;
}

@Injectable({ providedIn: "root" })
export class PadlockService {
  private tenantLogoCache = new Map<string, Observable<Blob>>();
  jwtHelper = new JwtHelperService();
  padlock$: Observable<PadlockTokenState> = this.store.select(
    selectPadlockTokenState,
  );
  padlockTokenState: PadlockTokenState;

  userIdKey = "149a63a1-d379-451a-993d-068d619bcb11";

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private store: Store<any>,
  ) {
    this.padlock$.subscribe((state) => (this.padlockTokenState = state));
  }

  login(email: string, password: string) {
    this.logger.debug(`PADLOCK SERVICE: login`);

    const url = `${environment.api_url_padlock}/tokens/jwt`;
    const payload = {
      email,
      password,
    };

    return this.http.post(url, payload);
  }

  loginB2C(b2cToken: string) {
    this.logger.debug(`PADLOCK SERVICE: login B2C`);
    const url = `${environment.api_url_padlock}/tokens/jwt-b2c`;
    let headers = new HttpHeaders();
    headers = headers.append("authorization", "Bearer " + b2cToken);

    return this.http.get(url, { headers });
  }

  isTokenValid(): boolean {
    let valid = false;
    try {
      const expired = this.jwtHelper.isTokenExpired(
        this.padlockTokenState.token,
      );
      valid = !expired;
    } catch (e) {
      this.logger.warn(`PADLOCK SERVICE: issue parsing token!`);
    }
    this.logger.debug(`PADLOCK SERVICE: token is ${valid ? "" : "NOT "}valid!`);
    return valid;
  }

  updateUserAndCheckIfChanged() {
    const persistedId = localStorage.getItem(this.userIdKey);
    const userId = this.getDecodedToken()?.sub;

    if (userId) {
      const changed = persistedId !== userId;

      localStorage.setItem(this.userIdKey, userId);
      return changed;
    }

    return false;
  }

  getMillisUntilExpiry(): number {
    const decoded = this.jwtHelper.decodeToken(this.padlockTokenState.token);
    const now = new Date().getTime();
    const expiryMillis = decoded.exp * 1000 - now;
    this.logger.debug(`PADLOCK SERVICE: token expires in ${expiryMillis} ms`);
    return expiryMillis;
  }

  getAuthToken() {
    return this.padlockTokenState.token;
  }

  getDecodedToken(token: string = this.padlockTokenState.token) {
    return this.jwtHelper.decodeToken(token);
  }

  createTenant(tenant) {
    const url = `${environment.api_url_padlock}/tenants`;
    const payload = tenant;

    this.logger.debug(`Create padlock tenant`, tenant);
    return this.http.post(url, payload);
  }

  deleteTenant(tenant) {
    const url = `${environment.api_url_padlock}/tenants/${tenant.uuid}`;

    this.logger.debug(`Delete padlock tenant`, tenant);
    return this.http.delete(url);
  }

  getTenantLogo(tenantUuid: string, version: number): Observable<Blob> {
    const cacheKey = `${tenantUuid}:${version}`;
    const cached = this.tenantLogoCache.get(cacheKey);
    if (cached) return cached;

    const url = `${environment.api_url_padlock}/tenants/${tenantUuid}/logo`;
    const request = this.http
      .get(url, {
        params: new HttpParams().set("v", version.toString()),
        responseType: "blob",
      })
      .pipe(
        catchError((error) => {
          this.tenantLogoCache.delete(cacheKey);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.tenantLogoCache.set(cacheKey, request);
    return request;
  }

  public getUserProfile(user: string) {
    return this.http.get(`${environment.api_url_padlock}/users/${user}`);
  }

  public updateUserProfile(user: string, body) {
    return this.http.put(`${environment.api_url_padlock}/users/${user}`, body);
  }

  public getUsers() {
    const url = `${environment.api_url_padlock}/users`;
    return this.http.get<PadlockUser[]>(url);
  }

  public getDirectoryUsers(search?: string) {
    const url = `${environment.api_url_padlock}/users/directory`;
    const params = search
      ? new HttpParams().set("user_name", search.trim())
      : undefined;
    return this.http.get(url, { params });
  }

  public getRoles() {
    const url = `${environment.api_url_padlock}/roles`;
    return this.http.get<PadlockRole[]>(url);
  }

  public removeUserFromTenant(tenantID: string, userID: string) {
    const url = `${environment.api_url_padlock}/tenants/${tenantID}/users/${userID}`;
    return this.http.delete(url);
  }

  public addUserToTenantByEmail(tenantID: string, email: string) {
    const payload = { user_email: email };
    const url = `${environment.api_url_padlock}/tenants/${tenantID}/users/by-email`;
    return this.http.post<{ user_uuid: string }>(url, payload);
  }

  public addUserRole(userID: string, roleID: string) {
    const payload = { user_uuid: userID };
    const url = `${environment.api_url_padlock}/roles/${roleID}/users`;
    return this.http.post(url, payload);
  }

  public resetUserPassword(userID: string) {
    const url = `${environment.api_url_padlock}/users/${encodeURIComponent(userID)}/reset-password`;
    return this.http.put(url, {});
  }

  public deleteUser(userID: string) {
    const url = `${environment.api_url_padlock}/users/${encodeURIComponent(userID)}`;
    return this.http.delete(url);
  }

  provisionUser(email: string, password: string, workspaceIDs: string[]) {
    this.logger.debug(`PADLOCK SERVICE: Provision User`);

    const url = `${environment.api_url_padlock}/users`;
    const payload = {
      email,
      password,
      tenants: workspaceIDs,
    };

    return this.http.post(url, payload);
  }

  changePassword(userID: string, password: string, newPassword: string) {
    const url = `${environment.api_url_padlock}/users/${userID}/change-password`;
    const payload = { password, newPassword };

    return this.http.put(url, payload);
  }
}
